import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import { Box, Button } from '@mui/material';
import React, { useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { compactMoney, date, DEMO_TODAY } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import { AboutThisPage, PageHeader, StatCard, StatGrid } from '../../ui/primitives';
import AttentionPanel from './AttentionPanel';
import DeadlinesPanel from './DeadlinesPanel';
import FundValueChart from './FundValueChart';
import MoneyFlowPanel from './MoneyFlowPanel';
import RecentActivityPanel from './RecentActivityPanel';

export default function DashboardPage() {
  const scope = useTenantScope();
  const { state } = useRegistry();
  const navigate = useNavigate();

  const metrics = useMemo(() => {
    const holdersWithUnits = new Set(scope.holdings.map((holding) => holding.holderId));
    const logins = state.investorLogins.filter((login) => login.tenantId === scope.tenant.id);
    const pipeline = scope.applications.filter(
      (item) => !['ISSUED', 'REJECTED', 'WITHDRAWN'].includes(item.status),
    );
    return {
      value: sumBy(scope.holdings, (holding) => holding.value),
      uncalled: sumBy(scope.holdings, (holding) => holding.uncalled ?? 0),
      holders: holdersWithUnits.size,
      logins: logins.length,
      approvals: scope.approvals.filter((item) => item.status === 'PENDING').length,
      kyc: scope.kycCases.filter((item) => item.status !== 'CLEARED').length,
      kycHigh: scope.kycCases.filter(
        (item) => item.status !== 'CLEARED' && item.severity === 'HIGH',
      ).length,
      bank: scope.bankLines.filter(
        (line) => line.status === 'UNMATCHED' || line.status === 'SUGGESTED',
      ),
      pipelineCount: pipeline.length,
      pipelineValue: sumBy(pipeline, (item) => item.amount),
    };
  }, [scope, state.investorLogins]);

  return (
    <>
      <PageHeader
        eyebrow={`${scope.tenant.name} · as at ${date(DEMO_TODAY)}`}
        title="Registry dashboard"
        subtitle="Everything the registry team needs to act on today, across every fund on the native register."
        actions={
          <>
            <Button component={RouterLink} to="/manager/calls/new" variant="outlined">
              New capital call
            </Button>
            <Button component={RouterLink} to="/manager/distributions/new" variant="contained">
              New distribution
            </Button>
          </>
        }
      />
      <AboutThisPage>
        <p>
          The dashboard reads straight from the registry ledger. Holdings are never typed in: they
          are the sum of issues, call payments, reinvestments, transfers and redemptions, so every
          number here can be traced back to individual ledger entries.
        </p>
        <p>
          Tiles open the queue behind them. Anything that moves money or units waits in Approvals
          for a second person.
        </p>
      </AboutThisPage>
      <StatGrid min={150}>
        <StatCard
          label="Value on register"
          value={compactMoney(metrics.value)}
          hint={
            metrics.uncalled > 0
              ? `${compactMoney(metrics.uncalled)} uncalled commitments`
              : undefined
          }
          icon={<SavingsOutlinedIcon fontSize="small" />}
          onClick={() => navigate('/manager/register')}
        />
        <StatCard
          label="Holders"
          value={metrics.holders}
          hint={`${metrics.logins} with investor app logins`}
          icon={<PeopleAltOutlinedIcon fontSize="small" />}
          onClick={() => navigate('/manager/holders/sample')}
        />
        <StatCard
          label="Awaiting approval"
          value={metrics.approvals}
          tone={metrics.approvals ? 'warning' : 'neutral'}
          hint="Maker-checker queue"
          icon={<AssignmentTurnedInOutlinedIcon fontSize="small" />}
          onClick={() => navigate('/manager/approvals')}
        />
        <StatCard
          label="KYC & AML exceptions"
          value={metrics.kyc}
          tone={metrics.kycHigh ? 'danger' : 'warning'}
          hint={`${metrics.kycHigh} high severity`}
          icon={<FactCheckOutlinedIcon fontSize="small" />}
          onClick={() => navigate('/manager/kyc')}
        />
        <StatCard
          label="Bank lines to match"
          value={metrics.bank.length}
          tone={metrics.bank.length ? 'warning' : 'neutral'}
          hint={compactMoney(sumBy(metrics.bank, (line) => line.amount))}
          icon={<ReceiptLongOutlinedIcon fontSize="small" />}
          onClick={() => navigate('/manager/payments')}
        />
        <StatCard
          label="Applications in flight"
          value={metrics.pipelineCount}
          tone="info"
          hint={`${compactMoney(metrics.pipelineValue)} pipeline`}
          icon={<HowToRegOutlinedIcon fontSize="small" />}
          onClick={() => navigate('/manager/applications')}
        />
      </StatGrid>
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, mb: 2 }}
      >
        <FundValueChart />
        <DeadlinesPanel />
      </Box>
      <Box
        sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, mb: 2 }}
      >
        <AttentionPanel />
        <MoneyFlowPanel />
      </Box>
      <RecentActivityPanel />
    </>
  );
}
